import { useEffect, useRef } from 'react';
import type { ReactElement } from 'react';
import type { ReactNode } from 'react';
import { Modal } from './Modal';

type DeleteConfirmationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  title: string;
  message: string;
  ariaLabel: string;
  confirmationValue: string;
  onConfirmationChange: (value: string) => void;
  isSubmitting: boolean;
  isSubmitDisabled: boolean;
  submitLabel?: string;
  submittingLabel?: string;
  confirmationPromptText?: string;
  confirmationPlaceholder?: string;
  errorMessage?: string | null;
  children?: ReactNode;
  panelClassName?: string;
};

export function DeleteConfirmationModal({
  isOpen,
  onClose,
  onSubmit,
  title,
  message,
  ariaLabel,
  confirmationValue,
  onConfirmationChange,
  isSubmitting,
  isSubmitDisabled,
  submitLabel = 'Submit',
  submittingLabel = 'Submitting...',
  confirmationPromptText = 'Type "delete" to confirm:',
  confirmationPlaceholder = 'delete',
  errorMessage = null,
  children,
  panelClassName,
}: DeleteConfirmationModalProps): ReactElement {
  const confirmationInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    confirmationInputRef.current?.focus();
  }, [isOpen]);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();

    if (isSubmitDisabled) {
      return;
    }

    onSubmit();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={ariaLabel}
      {...(panelClassName ? { panelClassName } : {})}
    >
      <form onSubmit={handleSubmit}>
        <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
        <p className="mt-2 text-sm text-slate-700">{message}</p>
        {errorMessage ? <p className="mt-2 text-sm text-rose-700">{errorMessage}</p> : null}
        {children ? <div className="mt-3">{children}</div> : null}

        <div className="mt-4">
          <label className="block text-sm text-slate-700">
            {confirmationPromptText}
            <input
              ref={confirmationInputRef}
              type="text"
              value={confirmationValue}
              onChange={(event) => onConfirmationChange(event.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
              placeholder={confirmationPlaceholder}
            />
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitDisabled}
            className="rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            {isSubmitting ? submittingLabel : submitLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}
